import Vue from 'vue';
import Vue2TouchEvents from 'vue2-touch-events';
import VueDragscroll from 'vue-dragscroll';
import get from 'lodash/get';

// base
import store from '@videotools/js/store';
import { initApi } from '@videotools/js/api/initApi';

// mixins and directives
import * as transMixin from '@videotools/js/mixins/transHelpers';
import * as debugMixin from '@videotools/js/mixins/debug';
import * as rafMixin from '@videotools/js/mixins/raf';
import * as confirmDialog from '@videotools/js/extend/confirm-dialog';
import directives from '@videotools/js/directives';

// internals
import Event from '@videotools/js/api/entities/Event';
import Helpers from '@legacy/js/helpers';
import { actions as sharedUserActions } from '@shared/js/store/modules/user';
import * as MUTATION from '@videotools/js/constants/mutations';
import { playerBus } from '@videotools/js/events';
import playerTypes from '@videotools/js/constants/playerTypes';
import { APP_MODE } from '@videotools/js/constants/app';
import LabelWithTooltip from '@components/LabelWithTooltip';

// extra
import $I18n from '@videotools/js/i18n';
import { getArenaConfig } from '@shared/utils/getArenaConfig';

const PLAYER_WAIT_INTERVAL = window.isTesting ? 2500 : 500;

const bootstrapVue = () => {
	Vue.use(Vue2TouchEvents);
	Vue.use(VueDragscroll);
	Vue.use(transMixin);
	Vue.use(debugMixin);
	Vue.use(rafMixin);
	Vue.use(confirmDialog);

	// used for embed mode
	Vue.component('LabelWithTooltip', LabelWithTooltip);

	for (const directive of directives) {
		directive.register(Vue);
	}

	Vue.prototype.$I18n = $I18n;
	Vue.prototype.$Helpers = new Helpers();
	Vue.prototype.$arenaConfig = getArenaConfig();
};

const initAppConfig = async(data, isEmbedPlayer) => {
	initApi(store);

	if (!data) return;

	if (window.$arenaConfig.loadArenaProfile) {
		store.dispatch(`sharedUser/${sharedUserActions.LOAD_PROFILE}`);
	} else {
		store.dispatch(`sharedUser/${sharedUserActions.SET_INITIALIZED}`);
	}

	if (window.$arenaConfig?.autoplay === false) {
		store.commit(`features/${MUTATION.SET_AUTOPLAY}`, false);
	}

	// Setup
	// if data is provided set app state to embedded and loaded
	if (isEmbedPlayer) {
		store.commit(`features/${MUTATION.SET_IS_EMBEDDED}`, true);
	}

	// Set password for restricted / non-listed events
	if (data.password) {
		store.commit(`event/${MUTATION.SET_PASSWORD}`, data.password);
	}

	// handle mode
	if (data.mode) {
		store.commit(`app/${MUTATION.SET_APP_MODE}`, data.mode);
	}

	// handle event
	if (data.event) {
		const wrappedEvent = new Event(data.event);
		await store.dispatch('event/setEvent', wrappedEvent);
	}

	if (data.lineup) store.commit(`event/${MUTATION.SET_EVENT_LINEUP}`, data.lineup);
	if (data.eventStats) store.commit(`event/${MUTATION.SET_EVENT_STATS}`, data.eventStats);
	if (data.leagueTable) store.commit(`event/${MUTATION.SET_LEAGUE_TABLE}`, data.leagueTable);
	if (data.tagTemplates) store.commit(`event/${MUTATION.SET_TAG_TEMPLATES}`, data.tagTemplates);

	// handle player type
	if (data.playerType) {
		if (data.playerType === playerTypes.LEAGUE) {
			store.commit(`app/${MUTATION.SET_PLAYER_TYPE}`, playerTypes.LEAGUE);
		} else {
			store.commit(`app/${MUTATION.SET_PLAYER_TYPE}`, playerTypes.CONTEXT);
		}
	}

	// play tag by tag ID
	if (data.eventTagId) {
		const tagId = Number.parseInt(data.eventTagId);
		let playerReady = false;
		let storeReady = false;
		let tag = null;
		const seekToTag = () => {
			if (playerReady && storeReady && tag) {
				setTimeout(() => {
					playerBus.seekTag(tag);
				}, PLAYER_WAIT_INTERVAL);
			}
		};
		// wait for player to be ready
		playerBus.subscribeReady(() => {
			playerReady = true;
			seekToTag();
		});
		// wait for tags to be loaded
		store.watch(
			(state) => state.event.tags,
			(tags) => {
				storeReady = true;
				tag = tags.find((_tag) => _tag.id === tagId);
				seekToTag();
			}
		);
	}

	if (data.autostartCouchmode) {
		store.commit(`features/${MUTATION.SET_AUTOSTART_COUCH_MODE}`, true);
	}

	const setFeature = (data, key, mutation) => {
		const value = get(data, key);
		if (typeof value === 'undefined') return;
		store.commit(`features/${mutation}`, value);
	};
	const setFeatures = (data, features) => {
		for (const [key, mutation] of features) {
			setFeature(data, key, mutation);
		}
	};

	// features
	if (data.features) {
		setFeatures(data.features, featureMutations);
	}
};

export const featureMutations = [
	['timeline.enable', MUTATION.SET_TIMELINE_ENABLED],
	['timeline.minimal', MUTATION.SET_TIMELINE_MINIMAL],
	['timeline.showTags', MUTATION.SET_TIMELINE_SHOW_TAGS],
	['player.autoplay', MUTATION.SET_AUTOPLAY],
	['player.time', MUTATION.SET_PLAYER_TIME_ENABLED],
	['player.liveBadge', MUTATION.SET_PLAYER_LIVE_BADGE_ENABLED],
	['player.logo', MUTATION.SET_LOGO_ENABLED],
	['player.seek', MUTATION.SET_PLAYER_SEEK_ENABLED],
	['player.pause', MUTATION.SET_PLAYER_PAUSE_ENABLED],
	['player.stop', MUTATION.SET_PLAYER_STOP_ENABLED],
	['player.contentInfoEnabled', MUTATION.SET_PLAYER_CONTENT_INFO_ENABLED],
	['player.forceDisableChromeCast', MUTATION.SET_PLAYER_FORCE_DISABLE_CHROMECAST],
	['player.playbackSpeedEnabled', MUTATION.SET_PLAYBACK_SPEED_ENABLED],
	['player.qualitySettingsEnabled', MUTATION.SET_QUALITY_SETTINGS_ENABLED],
	['player.forceOverlayVisible', MUTATION.SET_FORCE_OVERLAY_VISIBLE],
	['sharing', MUTATION.SET_SHARING_ENABLED],
	['loopedBackgroundMode', MUTATION.SET_LOOP_BACKGROUND_MODE],
	['autostartCouchMode', MUTATION.SET_AUTOSTART_COUCH_MODE],
];

export const initApp = async(appType, App, router, data = null) => {
	const isEmbedPlayer = appType === 'embed';
	const isFullPagePlayer = appType === 'fullpage';
	const containerSelector = isFullPagePlayer ? '#full-page-mode' : '#embed-view-mode';

	bootstrapVue();

	await initAppConfig(data, isEmbedPlayer);

	const vueInstanceParams = {
		el:     containerSelector,
		store,
		render: (h) => h(App),
	};

	if (router && !window.isSmartTv) {
		vueInstanceParams.router = router;
	}

	const _app = new Vue(vueInstanceParams);

	const api = {
		// internal
		_app,
		_store:  store,
		_player: playerBus,
		// app mode
		get mode() {
			return store.getters['app/appMode'];
		},
		set mode(value) {
			if (!Object.values(APP_MODE).includes(value)) throw new Error(`Invalid app mode: ${value}`);
			store.commit(`app/${MUTATION.SET_APP_MODE}`, value);
		},
		getMode: () => store.getters['app/appMode'],
		setMode(value) {
			let val = value;
			if (typeof value === 'function') val = value(APP_MODE);
			store.commit(`app/${MUTATION.SET_APP_MODE}`, val);
		},
		// features
		get features() {
			// proxy
			const storeFeatures = store.getters['features/features'];

			return new Proxy(storeFeatures, {
				get(_, k) {
					if (!k) return storeFeatures;
					if (k in storeFeatures) return storeFeatures[k];
					return null;
				},
				set(_, k, v) {
					if (typeof k !== 'string' || storeFeatures[k] === v) return true;
					api.setFeature(k, v);
					return true;
				},
			});
		},
		isFeatureEnabled: (key) => get(store.getters['features/features'], key),
		setFeature(key, value) {
			let mutation = null;
			for (const [featureKey, featureMutation] of featureMutations) {
				if (featureKey === key) {
					mutation = featureMutation;
					break;
				}
			}
			if (!mutation) throw new Error(`Unable to find mutation for feature: ${key}`);
			store.commit(`features/${mutation}`, value);
		},
		enableFeature:  (key) => api.setFeature(key, true),
		disableFeature: (key) => api.setFeature(key, false),
		// playback
		play:           () => playerBus.play(),
		pause:          () => playerBus.pause(),
		seek:           (seconds) => playerBus.seek(seconds),
		mute:           () => store.commit('player/setMute', true),
		unmute:         () => store.commit('player/setMute', false),
		// getters
		getPosition:    () => playerBus.position,
		getDuration:    () => playerBus.duration,
	};

	// Helper
	window.player = api;

	return api;
};
